import { useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useAddressLookup } from '../hooks/useAddressLookup';
import { AddressInput } from './AddressInput';
import { NetworkSelector } from './NetworkSelector';
import { QuickView } from './QuickView';
import { ExplorerLink } from './ExplorerLink';

export function BlockchainLookup() {
  const { loading, error, addressInfo, selectedNetwork, lookupAddress, reset, clearError } =
    useAddressLookup();

  // Show error as toast and clear it from state
  useEffect(() => {
    if (error) {
      toast.error(error, {
        duration: 4000,
        position: 'bottom-center',
      });
      // Clear error after showing toast to prevent duplicate toasts
      clearError();
    }
  }, [error, clearError]);

  const handleLookup = (address: string) => {
    lookupAddress(address);
  };

  const handleNetworkChange = (network: typeof selectedNetwork) => {
    if (!network || !addressInfo) return;
    // No cambiar el network aquí - dejar que lookupAddress lo actualice solo si tiene éxito
    lookupAddress(addressInfo.address, network);
  };

  return (
    <div className="max-w-5xl mx-auto">
      <Toaster
        toastOptions={{
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-color)',
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
          },
        }}
      />

      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-legal-900 dark:text-white mb-3">
          Blockchain Address Lookup
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Search blockchain addresses on Ethereum and Avalanche
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          No need to know which explorer to use - we'll find it for you
        </p>
      </div>

      <div className="bg-white dark:bg-legal-800 rounded-xl shadow-md p-6 sm:p-8 mb-6 border border-legal-100 dark:border-legal-700">
          <AddressInput onSubmit={handleLookup} loading={loading} />

          {loading && (
            <div className="mt-6 flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Searching across networks...</p>
              </div>
            </div>
          )}

          {addressInfo && selectedNetwork && !loading && (
            <div className="mt-6 space-y-6">
              {addressInfo.address && (
                <NetworkSelector
                  selectedNetwork={selectedNetwork}
                  onNetworkChange={handleNetworkChange}
                  address={addressInfo.address}
                />
              )}

              <QuickView addressInfo={addressInfo} />

              <ExplorerLink addressInfo={addressInfo} />

              <button
                onClick={reset}
                className="w-full sm:w-auto px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-legal-700 rounded-lg hover:bg-gray-200 dark:hover:bg-legal-600 transition-colors"
              >
                Search Another Address
              </button>
            </div>
          )}
        </div>

        <div className="bg-blue-50 dark:bg-legal-800 border border-blue-200 dark:border-legal-700 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-3">
            Supported Networks
          </h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              'Ethereum',
              'Avalanche',
            ].map((network) => (
              <div
                key={network}
                className="flex items-center gap-2 text-blue-800 dark:text-blue-300"
              >
                <svg
                  className="w-4 h-4 text-blue-600 dark:text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                {network}
              </div>
            ))}
          </div>
        </div>
    </div>
  );
}
